import { MEMBER_EXPERIENCE_KEYS } from "./memberFeatureCatalog.js";

const own = (value, key) => Object.prototype.hasOwnProperty.call(value, key);
const object = value => value !== null && typeof value === "object" && !Array.isArray(value);
export function contextId(value) {
  if (typeof value !== "number" && !(typeof value === "string" && /^[1-9][0-9]*$/.test(value))) return null;
  const id = Number(value);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
}

/** Host-selected context is authoritative. Lists may resolve its ID, never select it. */
export function resolveMemberScope({ currentUser, selectedClub, tenantContext }) {
  const invalid = message => ({ valid: false, message, fingerprint: "invalid" });
  const userId = contextId(currentUser?.id);
  if (!userId || !object(tenantContext)) return invalid("Autorisierter Benutzer- und Clubkontext fehlt.");
  if (own(tenantContext, "user_id") && contextId(tenantContext.user_id) !== userId) return invalid("Benutzerkontext stimmt nicht mit OPS überein.");
  const nested = tenantContext.club;
  const clubId = own(tenantContext, "club_id") ? contextId(tenantContext.club_id) : contextId(nested?.id);
  if (!clubId) return invalid("Bitte zuerst einen Club im OPS-Kontext auswählen.");
  if (nested != null && contextId(nested.id) !== clubId) return invalid("Widersprüchliche Clubzuordnung im OPS-Kontext.");
  if (selectedClub != null && contextId(selectedClub.id) !== clubId) return invalid("Ausgewählter Club stimmt nicht mit dem OPS-Kontext überein.");
  const candidates = Array.isArray(tenantContext.available_clubs)
    ? tenantContext.available_clubs.filter(row => contextId(row?.id) === clubId) : [];
  if (candidates.length > 1) return invalid("Clubzuordnung ist nicht eindeutig.");
  const club = selectedClub ?? nested ?? candidates[0];
  const organizationId = own(tenantContext, "organization_id")
    ? contextId(tenantContext.organization_id) : contextId(nested?.organization_id);
  if (!object(club) || !organizationId || contextId(club.organization_id) !== organizationId ||
      (nested != null && contextId(nested.organization_id) !== organizationId)) return invalid("Organisations- und Clubkontext sind unvollständig oder widersprüchlich.");
  if (tenantContext.scope_key != null && typeof tenantContext.scope_key !== "string") return invalid("Ungültiger OPS-Kontextschlüssel.");
  return {
    valid: true, userId, clubId, organizationId, club: { ...club },
    fingerprint: JSON.stringify([userId, organizationId, clubId, tenantContext.scope_key ?? null]),
  };
}

function cloneJson(value) {
  // Configuration originates from JSON; reject data that cannot survive the host contract.
  const visit = (v, seen = new Set()) => {
    if (v === null || ["string", "boolean"].includes(typeof v)) return;
    if (typeof v === "number" && Number.isFinite(v)) return;
    if (typeof v !== "object" || seen.has(v) || (!Array.isArray(v) && ![Object.prototype, null].includes(Object.getPrototypeOf(v)))) throw new Error("Ungültige Feature-Konfiguration.");
    seen.add(v);
    for (const child of Object.values(v)) visit(child, seen);
    seen.delete(v);
  };
  visit(value);
  return JSON.parse(JSON.stringify(value));
}

export function validateFeatureRows(rows, scope) {
  if (!Array.isArray(rows) || rows.length === 0) throw new Error("OPS hat keine verifizierbare Club-Feature-Liste geliefert.");
  const keys = new Set();
  return rows.map(row => {
    if (!object(row) || typeof row.feature_key !== "string" || !/^[a-z0-9_-]{1,100}$/.test(row.feature_key) ||
        typeof row.enabled !== "boolean" || !object(row.configuration) || keys.has(row.feature_key)) throw new Error("Ungültiger oder mehrdeutiger Club-Feature-Vertrag.");
    keys.add(row.feature_key);
    for (const [key, expected] of [["club_id", scope.clubId], ["organization_id", scope.organizationId]]) {
      if (own(row, key) && contextId(row[key]) !== expected) throw new Error("Feature-Antwort gehört zu einem anderen Clubkontext.");
    }
    for (const key of ["installed", "configured", "healthy", "ready"]) {
      if (row[key] != null && typeof row[key] !== "boolean") throw new Error("Ungültiger Betriebsstatus für " + row.feature_key + ".");
    }
    return { ...row, configuration: cloneJson(row.configuration) };
  });
}

export function scopedOrganizations(rows, scope) {
  if (!Array.isArray(rows)) throw new Error("Ungültige Organisationsliste.");
  const matches = rows.filter(row => contextId(row?.id) === scope.organizationId);
  if (matches.length !== 1) throw new Error("Organisation ist nicht eindeutig in der OPS-Antwort enthalten.");
  return matches.map(row => ({ ...row }));
}

export function scopedVenues(rows, scope) {
  if (!Array.isArray(rows)) throw new Error("Ungültige Venue-Liste.");
  const ids = new Set();
  return rows.map(row => {
    const id = contextId(row?.id);
    if (!id || ids.has(id) || contextId(row.club_id) !== scope.clubId ||
        (own(row, "organization_id") && contextId(row.organization_id) !== scope.organizationId)) throw new Error("Venue-Antwort gehört nicht eindeutig zum aktuellen Club.");
    ids.add(id);
    return { ...row };
  });
}

export function verifiedClub(rows, scope) {
  if (!Array.isArray(rows)) throw new Error("Ungültige Club-Liste.");
  const matches = rows.filter(row => contextId(row?.id) === scope.clubId);
  if (matches.length !== 1 || contextId(matches[0].organization_id) !== scope.organizationId) throw new Error("Aktueller Club ist nicht eindeutig in der OPS-Verwaltung bestätigt.");
  return { ...scope.club, ...matches[0] };
}

/** An item forwarded by the host has no scope envelope; fetch fresh evidence. */
export function scopedMemberRuntime(response, scopeKey) {
  if (typeof scopeKey !== "string" || !scopeKey.trim() || !object(response) || response.abi_version !== 1 ||
      response.scope_key !== scopeKey || !Array.isArray(response.addons)) {
    return { runtime: null, warning: "Addon-Betriebsstatus für diesen OPS-Kontext nicht verifiziert." };
  }
  const matches = response.addons.filter(row => row?.addon_id === "lenox.memberapp");
  if (matches.length !== 1) throw new Error("MEMBER-Addon ist im aktuellen OPS-Kontext nicht eindeutig verfügbar.");
  const runtime = matches[0];
  if (runtime.version !== "0.7.0-rc6") throw new Error("MEMBER-Addon-Version stimmt nicht mit dieser Verwaltung überein.");
  if (runtime.status !== "active" || runtime.enabled !== true) throw new Error("MEMBER-Addon ist im aktuellen OPS-Kontext nicht aktiv.");
  if (!Array.isArray(runtime.capabilities) || !runtime.capabilities.includes("member_app.admin")) {
    throw new Error("MEMBER-Verwaltungsberechtigung ist im aktuellen OPS-Kontext nicht bestätigt.");
  }
  return { runtime: { ...runtime, capabilities: [...runtime.capabilities] }, warning: null };
}

export function changeFeatureEnabled(rows, key, enabled) {
  if (!MEMBER_EXPERIENCE_KEYS.includes(key) || typeof enabled !== "boolean") return rows;
  return rows.map(row => row.feature_key === key ? { ...row, enabled, ready: null } : row);
}

export function changedFeaturePayload(baseline, draft) {
  const saved = new Map(baseline.map(row => [row.feature_key, row]));
  return draft.filter(row => MEMBER_EXPERIENCE_KEYS.includes(row.feature_key) && saved.has(row.feature_key) && row.enabled !== saved.get(row.feature_key).enabled)
    .map(row => ({ feature_key: row.feature_key, enabled: row.enabled, configuration: cloneJson(saved.get(row.feature_key).configuration) }));
}

export function validateSaveResponse(rows, scope, baseline, sent) {
  const result = validateFeatureRows(rows, scope);
  const index = new Map(result.map(row => [row.feature_key, row]));
  if (baseline.some(row => !index.has(row.feature_key)) || sent.some(row => index.get(row.feature_key)?.enabled !== row.enabled)) throw new Error("OPS hat die Moduländerung nicht vollständig bestätigt. Bitte neu laden.");
  return result;
}
